import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useIntl } from "../../../contexts/IntlContext";
import Input from "../../../components/ui/Input";
import LoadingButton from "../../../components/ui/LoadingButton";
import Switch from "../../../components/ui/Switch";

export type AccountFormValues = { name: string; scheduleLimit: number; active: boolean };

type Props = {
  open: boolean;
  titleId: string;
  initialValues: AccountFormValues;
  onClose: () => void;
  onSubmit: (values: AccountFormValues) => Promise<void>;
  /** Campos travados na UI (ex.: conta de proprietário — só o limite de agendamentos é editável). */
  disabledFields?: Array<"name" | "active">;
};

export default function YoutubeAccountFormDialog({
  open,
  titleId,
  initialValues,
  onClose,
  onSubmit,
  disabledFields,
}: Props) {
  const intl = useIntl();
  const [values, setValues] = useState<AccountFormValues>(initialValues);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async () => {
    if (!values.name.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{intl.formatMessage({ id: titleId })}</DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Input
            autoFocus
            disabled={disabledFields?.includes("name")}
            label={intl.formatMessage({ id: "youtubeAdminAccounts.form.name" })}
            value={values.name}
            onChange={e => setValues(v => ({ ...v, name: e.target.value }))}
          />
          <Input
            type="number"
            inputProps={{ min: 1, max: 10 }}
            label={intl.formatMessage({ id: "youtubeAdminAccounts.form.scheduleLimit" })}
            value={values.scheduleLimit}
            onChange={e => setValues(v => ({ ...v, scheduleLimit: Number(e.target.value) || 0 }))}
          />
          <Switch
            label={intl.formatMessage({ id: "youtubeAdminAccounts.form.active" })}
            switchProps={{
              checked: values.active,
              disabled: disabledFields?.includes("active"),
              onChange: e => setValues(v => ({ ...v, active: e.target.checked })),
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <LoadingButton onClick={onClose} variant="text" size="small" fullWidth={false}>
          {intl.formatMessage({ id: "common.cancel" })}
        </LoadingButton>
        <LoadingButton
          onClick={handleSubmit}
          variant="contained"
          size="small"
          fullWidth={false}
          loading={submitting}
          disabled={!values.name.trim()}
        >
          {intl.formatMessage({ id: "common.save" })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
